import type { AxiosInstance } from 'axios';
import type {
  PaginatedResult,
  AccessPackage,
  CompanyAccessPackageItem,
  CompanyResource,
  GetAccessPackagesParams,
  GetCompanyAccessPackagesParams,
  CreateCompanyAccessPackagePayload,
  UpdateCompanyAccessPackagePayload,
  GetCompanyResourcesParams,
} from './contracts';

/**
 * Gateway for interacting with the Access Hub API.
 */
export class AccessHubGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Retrieves a paginated list of access packages.
   * @param params - Optional filters and pagination options.
   * @returns A paginated result of access packages.
   */
  async getAccessPackages(params?: GetAccessPackagesParams): Promise<PaginatedResult<AccessPackage>> {
    const { data } = await this.httpClient.get<PaginatedResult<AccessPackage>>(`${this.baseUrl}/access-package`, {
      params,
    });
    return data;
  }

  /**
   * Retrieves a paginated list of company access packages.
   * @param params - Optional filters and pagination options.
   * @returns A paginated result of company access package items.
   */
  async getCompanyAccessPackages(
    params?: GetCompanyAccessPackagesParams,
  ): Promise<PaginatedResult<CompanyAccessPackageItem>> {
    const { data } = await this.httpClient.get<PaginatedResult<CompanyAccessPackageItem>>(
      `${this.baseUrl}/company-access-packages`,
      { params },
    );
    return data;
  }

  /**
   * Creates a new company access package.
   * @param payload - The company access package creation payload.
   * @returns The created company access package item.
   */
  async createCompanyAccessPackage(payload: CreateCompanyAccessPackagePayload): Promise<CompanyAccessPackageItem> {
    const { data } = await this.httpClient.post<CompanyAccessPackageItem>(
      `${this.baseUrl}/company-access-packages`,
      payload,
    );
    return data;
  }

  /**
   * Updates an existing company access package.
   * @param id - The ID of the company access package to update.
   * @param payload - The update payload.
   * @returns The updated company access package item.
   */
  async updateCompanyAccessPackage(
    id: string,
    payload: UpdateCompanyAccessPackagePayload,
  ): Promise<CompanyAccessPackageItem> {
    const { data } = await this.httpClient.patch<CompanyAccessPackageItem>(
      `${this.baseUrl}/company-access-packages/${id}`,
      payload,
    );
    return data;
  }

  /**
   * Retrieves company resources.
   * @param params - Filters for company resources.
   * @returns A list of company resources.
   */
  async getCompanyResources(params: GetCompanyResourcesParams): Promise<CompanyResource[]> {
    const { data } = await this.httpClient.get<CompanyResource[]>(`${this.baseUrl}/company-resources`, { params });
    return data;
  }
}

export type {
  PaginatedResult,
  AccessGroup,
  AccessPackageGroup,
  AccessPackage,
  CompanyAccessPackageItem,
  CompanyResource,
  GetAccessPackagesParams,
  GetCompanyAccessPackagesParams,
  CreateCompanyAccessPackagePayload,
  UpdateCompanyAccessPackagePayload,
  GetCompanyResourcesParams,
} from './contracts';
